import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { http } from "../../lib/http";
import PageChrome from "../../components/shell/PageChrome";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { FileText, Send, CheckCircle2, Ban, Pencil, Plus, Download, Eye, CreditCard } from "lucide-react";
import ExportMenu from "../../components/ExportMenu";
import { useAuth } from "../../contexts/AuthContext";
import PayNowModal from "./PayNowModal";
import TablePagination from "../../components/data/TablePagination";

const eur = (v) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(Number(v) || 0);
const STATUS_COLOR = {
  draft:     "bg-slate-200 text-slate-700",
  issued:    "bg-sky-100 text-sky-800",
  paid:      "bg-emerald-100 text-emerald-800",
  overdue:   "bg-[#C8102E] text-white",
  cancelled: "bg-zinc-200 text-zinc-600 line-through",
};

function StatusPill({ status, t }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[status] || "bg-slate-100"}`} data-testid={`inv-status-${status}`}>
      {t(`invoices.status.${status}`) || status}
    </span>
  );
}

function InvoiceCreateModal({ open, onClose, families, onCreated, t }) {
  const [familyId, setFamilyId] = useState("");
  const [series, setSeries] = useState("A");
  const [paymentMethod, setPaymentMethod] = useState("transfer");
  const [lines, setLines] = useState([{ description: "Mensualidad Mayo 2026", quantity: 1, unit_price: 80, vat_rate: 0 }]);
  const [saving, setSaving] = useState(false);
  const reset = () => {
    setFamilyId(""); setSeries("A"); setPaymentMethod("transfer");
    setLines([{ description: "Mensualidad Mayo 2026", quantity: 1, unit_price: 80, vat_rate: 0 }]);
  };
  const setLine = (i, k, v) => setLines(ls => ls.map((l, j) => j === i ? { ...l, [k]: v } : l));
  const addLine = () => setLines(ls => [...ls, { description: "", quantity: 1, unit_price: 0, vat_rate: series === "A" ? 0 : 21 }]);
  const total = useMemo(() => lines.reduce((s, l) => {
    const sub = Number(l.quantity || 0) * Number(l.unit_price || 0);
    return s + sub + sub * Number(l.vat_rate || 0) / 100;
  }, 0), [lines]);
  const submit = async () => {
    if (!familyId) { toast.error(t("invoices.pickFamily")); return; }
    setSaving(true);
    try {
      const { data } = await http.post("/invoices", { family_id: familyId, series, payment_method: paymentMethod, line_items: lines, auto_apply_discounts: true });
      toast.success(t("invoices.draftCreated") + " · " + (data.invoice_number || data.id.slice(0, 8)));
      onCreated(data); onClose(); reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.messages?.[0] || t("invoices.createFailed"));
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("invoices.create")}</DialogTitle>
          <DialogDescription>{t("invoices.createSubtitle")}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs uppercase font-semibold text-slate-500">{t("invoices.family")}</label>
            <Select value={familyId} onValueChange={setFamilyId}>
              <SelectTrigger data-testid="inv-family-select"><SelectValue placeholder={t("common.search")} /></SelectTrigger>
              <SelectContent className="max-h-72">
                {families.map(f => <SelectItem key={f.id} value={f.id}>{f.family_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase font-semibold text-slate-500">{t("invoices.series")}</label>
            <Select value={series} onValueChange={setSeries}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="A">A — Educational (IVA 0%)</SelectItem>
                <SelectItem value="B">B — Materials (IVA)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase font-semibold text-slate-500">{t("invoices.paymentMethod")}</label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["transfer","sepa","card","cash"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3">
          <div className="text-xs uppercase font-semibold text-slate-500 mb-1">{t("invoices.lineItems")}</div>
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2">
                <Input className="col-span-5" value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} placeholder={t("invoices.description")} />
                <Input className="col-span-1" type="number" step="0.01" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} />
                <Input className="col-span-2" type="number" step="0.01" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} placeholder="€" />
                <Select value={String(l.vat_rate)} onValueChange={(v) => setLine(i, "vat_rate", Number(v))}>
                  <SelectTrigger className="col-span-2"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[0, 4, 10, 21].map(r => <SelectItem key={r} value={String(r)}>{r}% IVA</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="ghost" className="col-span-2" onClick={() => setLines(ls => ls.filter((_, j) => j !== i))}>{t("common.delete")}</Button>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" className="mt-2" onClick={addLine}><Plus className="h-4 w-4 mr-1"/>{t("invoices.addLine")}</Button>
        </div>
        <div className="text-right mt-2 text-lg font-semibold text-[#0A1F44]" data-testid="inv-modal-total">
          {t("invoices.total")}: <span className="text-[#C8102E]">{eur(total)}</span>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="inv-modal-save">{saving ? "…" : t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarkPaidModal({ invoice, open, onClose, onPaid, t }) {
  const [paidDate, setPaidDate] = useState(new Date().toISOString().slice(0,10));
  const [amount, setAmount] = useState(invoice?.total || 0);
  const [pm, setPm] = useState(invoice?.payment_method || "transfer");
  useEffect(() => { setAmount(invoice?.total || 0); setPm(invoice?.payment_method || "transfer"); }, [invoice]);
  const submit = async () => {
    try {
      const { data } = await http.post(`/invoices/${invoice.id}/mark-paid`, {
        paid_date: paidDate, paid_amount: Number(amount), payment_method: pm,
      });
      toast.success(t("invoices.markedPaid") + " · " + (data.invoice_number || data.id.slice(0,8)));
      onPaid(data); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Mark paid failed");
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("invoices.markPaid")} · {invoice?.invoice_number}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div><label className="text-xs">{t("invoices.paidDate")}</label><Input type="date" value={paidDate} onChange={(e)=>setPaidDate(e.target.value)} /></div>
          <div><label className="text-xs">{t("invoices.amount")}</label><Input type="number" step="0.01" value={amount} onChange={(e)=>setAmount(e.target.value)} data-testid="markpaid-amount"/></div>
          <div>
            <label className="text-xs">{t("invoices.paymentMethod")}</label>
            <Select value={pm} onValueChange={setPm}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["transfer","sepa","card","cash"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="markpaid-confirm">{t("invoices.confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function InvoicesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [families, setFamilies] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payNowOpen, setPayNowOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  const load = useCallback(async () => {
    const { data } = await http.get("/invoices?limit=500");
    setItems(data.items || []);
  }, []);

  useEffect(() => {
    load();
    if (user.role !== "parent") http.get("/families?limit=500").then(({ data }) => setFamilies(data.items || []));
  }, [load, user.role]);

  const filtered = useMemo(() => items.filter(i =>
    (statusFilter === "all" || i.status === statusFilter) &&
    (!q || (i.invoice_number || "").toLowerCase().includes(q.toLowerCase()) || (i.issued_to_name || "").toLowerCase().includes(q.toLowerCase()))
  ), [items, statusFilter, q]);

  useEffect(() => { setPage(1); }, [q, statusFilter]);
  const paged = useMemo(() => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page]);

  const issue = async (inv) => {
    try { const { data } = await http.post(`/invoices/${inv.id}/issue`);
      toast.success(t("invoices.issued") + " · " + data.invoice_number); load();
    } catch (e) { toast.error(e?.response?.data?.detail?.error || "Issue failed"); }
  };
  const sendEmail = async (inv) => {
    try { await http.post(`/invoices/${inv.id}/send-email`);
      toast.info(t("invoices.emailMocked")); } catch (e) { toast.error("send failed"); }
  };
  const cancel = async (inv) => {
    const reason = prompt(t("invoices.cancelReason"));
    if (!reason) return;
    try {
      const { data } = await http.post(`/invoices/${inv.id}/cancel`, { reason });
      toast.success(t("invoices.cancelled") + " · " + t("invoices.creditNote") + " " + data.credit_note?.invoice_number);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail?.error || "cancel failed"); }
  };
  const openPdf = async (inv) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("eata.access_token");
    const r = await fetch(`${base}/api/invoices/${inv.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return toast.error("PDF failed");
    const blob = await r.blob(); window.open(URL.createObjectURL(blob), "_blank");
  };

  const isParent = user.role === "parent";
  return (
    <PageChrome title={t("invoices.title")} subtitle={t("invoices.subtitle")} testId="invoices-page">
      <div className="flex flex-wrap gap-2 items-center mb-3">
        <Input placeholder={t("common.search")} value={q} onChange={(e)=>setQ(e.target.value)} className="max-w-xs bg-white" data-testid="inv-search" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 bg-white" data-testid="inv-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")}</SelectItem>
            {["draft","issued","paid","overdue","cancelled"].map(s => <SelectItem key={s} value={s}>{t(`invoices.status.${s}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex gap-2 items-center">
          <ExportMenu resource="invoices" extraParams={{ status: statusFilter === "all" ? "" : statusFilter }} filename="invoices"/>
          {!isParent && (<Button onClick={()=>setCreateOpen(true)} data-testid="inv-create-btn" className="bg-[#0A1F44]">
            <Plus className="h-4 w-4 mr-1"/>{t("invoices.newInvoice")}
          </Button>)}
        </div>
      </div>
      <div className="mb-3 flex justify-end">
        <TablePagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onPageChange={setPage} testid="invoices-pagination-top" />
      </div>
      <Card className="overflow-hidden border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">#</th>
              <th className="text-left px-3 py-2">{t("invoices.issuedTo")}</th>
              <th className="text-left px-3 py-2">{t("invoices.issueDate")}</th>
              <th className="text-left px-3 py-2">{t("invoices.dueDate")}</th>
              <th className="text-right px-3 py-2">{t("invoices.total")}</th>
              <th className="text-left px-3 py-2">{t("invoices.status_")}</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="inv-table">
            {paged.map(inv => (
              <tr key={inv.id} className="hover:bg-slate-50" data-testid={`inv-row-${inv.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{inv.invoice_number || <span className="text-slate-400">{t("invoices.draft")}</span>}</td>
                <td className="px-3 py-2">{inv.issued_to_name}</td>
                <td className="px-3 py-2">{inv.issue_date}</td>
                <td className="px-3 py-2">{inv.due_date}</td>
                <td className="px-3 py-2 text-right font-semibold">{eur(inv.total)}</td>
                <td className="px-3 py-2"><StatusPill status={inv.status} t={t}/></td>
                <td className="px-3 py-2">
                  <div className="flex gap-1 justify-end">
                    {isParent && ["issued","overdue"].includes(inv.status) && (
                      <Button size="sm" onClick={()=>{ setActive(inv); setPayNowOpen(true); }} className="bg-[#C8102E] hover:bg-[#a40c25]" data-testid={`inv-paynow-${inv.id}`}>
                        <CreditCard className="h-3 w-3 mr-1"/>{t("payments.payNow")}
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={()=>openPdf(inv)} title="PDF" data-testid={`inv-pdf-${inv.id}`}><FileText className="h-4 w-4"/></Button>
                    {!isParent && inv.status === "draft" && (
                      <Button variant="ghost" size="icon" onClick={()=>{ if (confirm(t("invoices.issueConfirm"))) issue(inv); }} title={t("invoices.issue")} data-testid={`inv-issue-${inv.id}`}><CheckCircle2 className="h-4 w-4 text-emerald-600"/></Button>
                    )}
                    {!isParent && ["issued","overdue"].includes(inv.status) && (
                      <Button variant="ghost" size="icon" onClick={()=>{ setActive(inv); setPayOpen(true); }} title={t("invoices.markPaid")} data-testid={`inv-pay-${inv.id}`}><CreditCard className="h-4 w-4 text-sky-600"/></Button>
                    )}
                    {!isParent && ["issued","overdue","paid"].includes(inv.status) && (
                      <Button variant="ghost" size="icon" onClick={()=>sendEmail(inv)} title={t("invoices.sendEmail")}><Send className="h-4 w-4"/></Button>
                    )}
                    {user.role === "admin" && ["issued","overdue","paid"].includes(inv.status) && (
                      <Button variant="ghost" size="icon" onClick={()=>cancel(inv)} title={t("invoices.cancel")} data-testid={`inv-cancel-${inv.id}`}><Ban className="h-4 w-4 text-rose-600"/></Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-slate-400">{t("common.noResults")}</td></tr>
            )}
          </tbody>
        </table>
      </Card>
      <div className="mt-4 flex justify-end">
        <TablePagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onPageChange={setPage} testid="invoices-pagination-bottom" />
      </div>

      <InvoiceCreateModal open={createOpen} onClose={()=>setCreateOpen(false)} families={families} onCreated={()=>load()} t={t}/>
      <MarkPaidModal invoice={active} open={payOpen} onClose={()=>setPayOpen(false)} onPaid={()=>load()} t={t}/>
      <PayNowModal invoice={active} open={payNowOpen} onClose={()=>setPayNowOpen(false)} onPaid={()=>load()}/>
    </PageChrome>
  );
}
